import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  View,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Keyboard,
} from 'react-native';
import {createSearchInputStyles} from '../../../../shared/components/ui/SearchInput/SearchInput.styles';
import {SearchInputProps} from '../../../../shared/components/ui/SearchInput/SearchInputProps';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import SearchIcon from '../../../../shared/assets/icons/SearchIcon';
import SearchResult from '../../../../shared/assets/icons/SearchResult';
import CloseIcon from '@shared/assets/icons/CloseIcon';
import Dropdown from '../Dropdown/Dropdown';
import {DropdownOption} from '../Dropdown/DropdownProps';
import {useCommonController} from '@shared/controllers/commonController';
import Recorder from '@shared/assets/icons/Recorder';
import {useAccessibilityProps} from '../../../../shared/hooks/useAccessibilityProps';
import {useDispatch} from 'react-redux';
import {showErrorToast} from '@shared/store/errorSlice';

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder,
  containerStyle,
  onClick,
  showArrowIcon = true,
  searchText,
  onClearSearch,
  showLeftDropdown = false,
  dropdownValues,
  selectedDropdownValue,
  selectedDropdownOption,
  dropdownContainerStyle,
  dropdownPlaceholder,
  onChangeText,
  requireValidation = false,
  validationType = 'alphanumeric',
  validationLength = 25,
  showRightIcon = false,
  hasLengthValidation = false,
  maxContainer,
  accessibilityLabel,
  accessibilityHint,
  ...rest
}) => {
  const {t} = useTranslation();
  const theme: any = useScreenTheme('searchInput');
  const styles = useMemo(() => createSearchInputStyles(theme), [theme]);
  const {sanitizeInput} = useCommonController();
  const a11yProps = useAccessibilityProps({
    accessibilityLabel,
    accessibilityHint,
  });

  const [value, setValue] = useState(searchText);
  const [selectedSort, setSelectedSort] = useState<DropdownOption | undefined>(
    dropdownPlaceholder || dropdownValues?.[0],
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const filterRef = React.useRef<View>(null);
  const containerRef = React.useRef<View>(null);
  const dispatch = useDispatch();

  /** Compute border color based on interaction state (Figma spec):
   *  focus → activeBorderColor (#005FCC)
   *  hover → hoverBorderColor  (#66ABFF)
   *  default → borderColor     (#CCE3FF)
   */
  const dynamicBorderColor = isFocused
    ? (theme as any).activeBorderColor ?? theme.borderColor
    : isHovered
    ? (theme as any).hoverBorderColor ?? theme.borderColor
    : theme.borderColor;

  // Sync internal state with searchText prop
  useEffect(() => {
    setValue(searchText);
  }, [searchText]);

  // When the parent provides a controlled selectedDropdownOption, keep
  // internal selectedSort in sync so it is correct after selectedDropdownOption
  // returns to undefined (e.g. when the parent resets the filter).
  useEffect(() => {
    if (selectedDropdownOption !== undefined) {
      setSelectedSort(selectedDropdownOption);
    }
  }, [selectedDropdownOption?.value]);

  // Deselect (clear focused/hovered) when the user interacts outside the
  // SearchInput so the border reverts to its default (deselected) appearance.
  // Web: detect mousedown outside the container element.
  // iOS/Android: detect keyboard dismissal (tap outside collapses the keyboard).
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleMouseDown = (e: MouseEvent) => {
        const node = containerRef.current as any;
        if (node && !node.contains(e.target as Node)) {
          setIsFocused(false);
          setIsHovered(false);
        }
      };
      document.addEventListener('mousedown', handleMouseDown);
      return () => document.removeEventListener('mousedown', handleMouseDown);
    } else {
      // On iOS/Android the keyboard hides whenever the user taps away from
      // the focused TextInput, which is the native equivalent of "click outside".
      const subscription = Keyboard.addListener('keyboardDidHide', () => {
        setIsFocused(false);
      });
      return () => subscription.remove();
    }
  }, []);

  // Compute the displayed dropdown value synchronously so the Dropdown
  // re-renders in the SAME cycle that selectedDropdownOption changes.
  // This avoids the one-render lag that the useEffect-only approach has.
  const displayedSort = selectedDropdownOption ?? selectedSort;

  const onClear = () => {
    setValue('');
    onClearSearch('');
  };

  /**
   * This method is used to set the value, If the entire text removed then we need to call the default api
   * @param {text}  - string
   * @returns {void}
   */
  const onValueChange = (text: string) => {
    let sanitizedText = text;

    // Apply validation only if requireValidation is true
    if (requireValidation) {
      sanitizedText = sanitizeInput(text, validationType);
    }
    setValue(sanitizedText);
    // Otherwise, accept all characters (default behavior)
    // If onChangeText is provided, let parent handle the value
    if (onChangeText) {
      onChangeText(sanitizedText);
      // Don't set internal value here, let it sync via useEffect from searchText prop
    } else {
      // Fallback to internal state management
      if (sanitizedText === '') {
        onClick(sanitizedText, false);
      }
    }
  };

  return (
    <View
      ref={containerRef}
      {...rest}
      style={[
        styles.container,
        containerStyle,
        {borderColor: dynamicBorderColor},
      ]}
      accessible={false}
      importantForAccessibility="no"
      // @ts-ignore — onMouseEnter/onMouseLeave are valid on React Native Web
      onMouseEnter={() => setIsHovered(true)}
      // @ts-ignore
      onMouseLeave={() => setIsHovered(false)}>
      <View style={styles.innerContainer} accessible={false}>
        {showLeftDropdown && (
          <Dropdown
            options={dropdownValues || []}
            selectedSort={displayedSort}
            onSelect={option => {
              if (value !== '') {
                setValue('');
              }
              setSelectedSort(option),
                selectedDropdownValue && selectedDropdownValue?.(option);
            }}
            customContainerStyles={[
              styles.sortDropdown,
              dropdownContainerStyle,
              {borderColor: dynamicBorderColor},
            ]}
            customTextStyles={styles.dropdownText}
            inputRef={filterRef}
            showTickMark={true}
            customLabelStyles={styles.customLabelStyle}
          />
        )}
        {Platform.OS === 'web' ? (
          <View style={[styles.rightView, maxContainer]} accessible={false}>
            <SearchIcon color={theme.searchIconColor} />
            <KeyboardAvoidingView style={{flex: 1}} behavior={undefined}>
              <TextInput
                value={value}
                placeholder={placeholder}
                style={[styles.input, {outline: 'none'} as any]}
                onChangeText={onValueChange}
                placeholderTextColor={theme.inputPlaceHolder}
                maxLength={hasLengthValidation ? validationLength : undefined}
                accessible={true}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                {...a11yProps}
              />
            </KeyboardAvoidingView>
          </View>
        ) : (
          <View style={[styles.rightView, maxContainer]} accessible={false}>
            <SearchIcon color={theme.searchIconColor} />
            <TextInput
              value={value}
              placeholder={placeholder}
              style={[styles.input, {outline: 'none'} as any]}
              onChangeText={onValueChange}
              placeholderTextColor={theme.inputPlaceHolder}
              maxLength={hasLengthValidation ? validationLength : undefined}
              accessible={true}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              {...a11yProps}
            />
          </View>
        )}
      </View>
      {showArrowIcon && (
        <View style={styles.leftContainer} accessible={false}>
          {showRightIcon && (
            <TouchableOpacity
              style={styles.closeIcon}
              onPress={() => {}}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('common.accessibility.voiceSearch')}
              // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
              tabIndex={0}
              focusable={true}>
              <Recorder
                bgColor="transparent"
                color={(theme as any).pinAuthCloseIconColor}
              />
            </TouchableOpacity>
          )}
          {value !== '' && (
            <TouchableOpacity
              style={styles.closeIcon}
              onPress={onClear}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={t('common.accessibility.clearSearch')}
              accessibilityHint={t('common.accessibility.clearSearchHint')}
              // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
              tabIndex={0}
              focusable={true}>
              <CloseIcon
                color={(theme as any).pinAuthCloseIconColor}
                height={40}
                width={40}
              />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={() => {
              if (showLeftDropdown) {
                const isDropdownUnselected =
                  !!dropdownPlaceholder &&
                  displayedSort?.value === dropdownPlaceholder?.value;
                const isTextEmpty = !value || value === '';
                if (isDropdownUnselected && isTextEmpty) {
                  dispatch(
                    showErrorToast({
                      toastMessage: t(
                        'common.globalToast.selectFilterAndEnterSearch',
                      ),
                    }),
                  );
                  return;
                }
                if (isDropdownUnselected) {
                  dispatch(
                    showErrorToast({
                      toastMessage: t('common.globalToast.selectFilter'),
                    }),
                  );
                  return;
                }
                if (isTextEmpty) {
                  dispatch(
                    showErrorToast({
                      toastMessage: t('common.globalToast.emptySearchText'),
                    }),
                  );
                  return;
                }
              } else if (!value || value === '') {
                dispatch(
                  showErrorToast({
                    toastMessage: t('common.globalToast.emptySearchText'),
                  }),
                );
                return;
              }
              onClick(value, true);
            }}
            style={{padding: 0, margin: 0}}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={t('common.accessibility.submitSearch')}
            accessibilityHint={t('common.accessibility.submitSearchHint')}
            // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
            tabIndex={0}
            focusable={true}>
            <View
              style={[
                styles.searchArrow,
                isFocused
                  ? styles.enableSearchArrow
                  : isHovered
                  ? styles.enableSearchHoverArrow
                  : undefined,
              ]}
              accessible={false}>
              <SearchResult color={theme.inputColor} />
            </View>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

export default SearchInput;
